import {
  AfterViewInit,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { User } from '@app/shared/models/user.model';
import { EventManager } from '@app/shared/utils/event-manager';
import { SharedModule } from '@app/shared/shared.module';
import { UserRepository } from '@app/shared/state/user/user.repository';
import { GsapService } from '@app/shared/animations/gsap.service';

export enum AnimationStep {
  Start = 0,
  ShowFlyingCurrency = 1,
  End = 2,
}

@Component({
  selector: 'slotcom-welcome-reward-modal',
  templateUrl: './welcome-reward-modal.component.html',
  styleUrls: ['./welcome-reward-modal.component.scss'],
  standalone: true,
  imports: [SharedModule],
})
export class WelcomeRewardModalComponent implements OnInit, AfterViewInit {
  readonly welcomeRewardModalData = inject<Modal>(MAT_DIALOG_DATA);

  currentStep = signal<AnimationStep>(AnimationStep.Start);
  user = signal<User>(null);

  private timerId?: ReturnType<typeof setTimeout>;

  private readonly STEP_DURATIONS: Record<AnimationStep, number> = {
    [AnimationStep.Start]: 0,
    [AnimationStep.ShowFlyingCurrency]: 3000,
    [AnimationStep.End]: 0,
  };

  @ViewChild('flyingGems') flyingGems!: ElementRef<HTMLDivElement>;
  @ViewChild('flyingCoins') flyingCoins!: ElementRef<HTMLDivElement>;
  @ViewChild('bottomCoins') bottomCoins!: ElementRef<HTMLDivElement>;
  @ViewChild('title') title!: ElementRef<HTMLDivElement>;
  @ViewChild('coinsDisplay') coinsDisplay?: ElementRef<HTMLDivElement>;
  @ViewChild('rewardsDisplay') rewardsDisplay?: ElementRef<HTMLDivElement>;

  private readonly gsapService = inject(GsapService);
  private readonly eventManager = inject(EventManager);
  private readonly userRepo = inject(UserRepository);

  private readonly _ = effect(() => {
    const step = this.currentStep();
    this.handleStep(step);
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.clearTimer());
    console.log(this.welcomeRewardModalData);
  }

  ngOnInit(): void {
    this.user.set(this.userRepo.getUser());
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      this.resetStage();
      this.currentStep.set(AnimationStep.ShowFlyingCurrency);
    });
  }

  closeModal(): void {
    this.eventManager.closeModal(new Modal(NonPersistedModalName.WelcomeRewardModalComponent, ModalType.View));
  }

  private resetStage(): void {
    this.setElements([this.flyingGems, this.flyingCoins, this.bottomCoins, this.title], { opacity: 0 });
  }

  private setElements(refs: (ElementRef<HTMLElement> | undefined)[], props: Record<string, unknown>): void {
    for (const ref of refs) {
      if (ref?.nativeElement) {
        this.gsapService.set(ref.nativeElement, props);
      }
    }
  }

  private handleStep(step: AnimationStep): void {
    this.clearTimer();

    switch (step) {
      case AnimationStep.Start:
        break;
      case AnimationStep.End:
        this.scheduleNext(step);
        break;
      case AnimationStep.ShowFlyingCurrency:
        this.gsapService.fromTo(
          this.flyingCoins.nativeElement,
          { opacity: 0, scale: 0 },
          { opacity: 1, scale: 1 },
          { duration: 0.5 }
        );

        this.gsapService.fromTo(
          this.flyingGems.nativeElement,
          { opacity: 0, scale: 0 },
          { opacity: 1, scale: 1 },
          { duration: 0.5 }
        );

        this.gsapService.fromTo(
          this.title!.nativeElement,
          { opacity: 0, scale: 0 },
          { opacity: 1, scale: 1 },
          { duration: 0.5 }
        );

        this.gsapService.fromTo(
          (this.rewardsDisplay ?? this.coinsDisplay)!.nativeElement,
          { opacity: 0, scale: 0 },
          { opacity: 1, scale: 1 },
          { duration: 0.5 }
        );
        this.scheduleNext(step);
        break;
    }
  }

  private scheduleNext(step: AnimationStep): void {
    const duration = this.STEP_DURATIONS[step];

    this.timerId = setTimeout(() => {
      if (this.currentStep() < AnimationStep.End) {
        this.currentStep.set(this.currentStep() + 1);
      } else if (this.currentStep() >= AnimationStep.End) {
        this.close();
      }
    }, duration);
  }

  private clearTimer(): void {
    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = undefined;
    }
  }

  close(): void {
    this.eventManager.closeModal(new Modal(NonPersistedModalName.WelcomeRewardModalComponent, ModalType.View));
  }
}
